import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type PushState = "unsupported" | "default" | "granted" | "denied" | "loading";

export function usePushNotifications() {
  const { user, session } = useAuth();
  const [pushState, setPushState] = useState<PushState>("loading");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const isSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  // Check current permission & auto-renew subscription on each visit
  useEffect(() => {
    if (!isSupported) {
      setPushState("unsupported");
      return;
    }

    const perm = Notification.permission;
    setPushState(perm as PushState);

    if (perm === "granted" && user && session) {
      // Auto-renew: re-subscribe silently to keep the subscription fresh
      autoRenewSubscription();
    } else if (perm === "granted") {
      checkExistingSubscription();
    }
  }, [isSupported, user, session]);

  const checkExistingSubscription = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch (err) {
      console.error("[Push] Error checking subscription:", err);
      setIsSubscribed(false);
    }
  }, []);

  // Silently re-subscribe to refresh the endpoint in the backend
  const autoRenewSubscription = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) return;

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      const subJson = subscription.toJSON();

      await supabase.functions.invoke("push-subscribe", {
        body: {
          action: "subscribe",
          subscription: { endpoint: subJson.endpoint, keys: subJson.keys },
          device_label: getDeviceLabel(),
        },
      });

      setIsSubscribed(true);
    } catch (err) {
      console.error("[Push] Auto-renew failed:", err);
      // Still check if there's an existing one
      checkExistingSubscription();
    }
  }, []);

  const getVapidPublicKey = async (): Promise<string | null> => {
    const { data, error } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "vapid_public_key")
      .eq("language", "es")
      .eq("market", "mx")
      .maybeSingle();

    if (error) {
      console.error("[Push] ✗ Error fetching VAPID key:", error.message);
      return null;
    }
    if (!data?.value) {
      console.error("[Push] ✗ VAPID public key not found in app_settings");
      return null;
    }
    return data.value;
  };

  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribe = useCallback(async () => {
    if (!isSupported || !user || !session) {
      console.warn("[Push] Cannot subscribe: supported=", isSupported, "user=", !!user, "session=", !!session);
      return false;
    }

    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPushState(permission as PushState);

      if (permission !== "granted") {
        console.warn("[Push] ✗ Permission not granted:", permission);
        setLoading(false);
        return false;
      }

      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) {
        console.error("[Push] ✗ No VAPID public key available");
        setLoading(false);
        return false;
      }

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      const subJson = subscription.toJSON();

      const { error } = await supabase.functions.invoke("push-subscribe", {
        body: {
          action: "subscribe",
          subscription: {
            endpoint: subJson.endpoint,
            keys: subJson.keys,
          },
          device_label: getDeviceLabel(),
        },
      });

      if (error) {
        console.error("[Push] ✗ Failed to save subscription:", error);
        setLoading(false);
        return false;
      }

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("[Push] ✗ Subscribe error:", err);
      setLoading(false);
      return false;
    }
  }, [isSupported, user, session]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;

    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();

      if (subscription) {
        const subJson = subscription.toJSON();

        // Remove from backend
        await supabase.functions.invoke("push-subscribe", {
          body: {
            action: "unsubscribe",
            subscription: { endpoint: subJson.endpoint },
          },
        });

        // Unsubscribe locally
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("[Push] ✗ Unsubscribe error:", err);
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Test function to send a test push to the current user
  const sendTestPush = useCallback(async () => {
    if (!user) return null;
    try {
      const { data, error } = await supabase.functions.invoke("send-push", {
        body: {
          user_id: user.id,
          title: "🧪 Teste de notificação",
          body: "Se você está vendo isso, o push funciona!",
          url: "/profile",
          tag: "test-push",
        },
      });

      return { data, error };
    } catch (err) {
      console.error("[Push] 🧪 Test push exception:", err);
      return { data: null, error: err };
    }
  }, [user]);

  return {
    pushState,
    isSubscribed,
    isSupported,
    loading,
    subscribe,
    unsubscribe,
    sendTestPush,
  };
}

function getDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown";
}
